import React, { useState, useEffect } from "react";
import {
  getAllTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeamsWithPlayerCount,
} from "../../Libs/api";
import Loader from "../../Component/Loader";
import { Search, Plus, Users, Trash2, Edit } from "lucide-react";
import Swal from "sweetalert2";

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    teamName: "",
    logo: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [shouldRenderModal, setShouldRenderModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await getTeamsWithPlayerCount();
      if (response.success) {
        setTeams(response.data);
      } else {
        setError("Failed to fetch teams");
      }
    } catch (error) {
      setError("Error fetching teams: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        Swal.fire("Error!", "Please select a valid image file", "error");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Error!", "Image size should be less than 5MB", "error");
        return;
      }

      setLogoFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      if (!formData.teamName.trim()) {
        Swal.fire("Error!", "Team name is required", "error");
        return;
      }

      setModalLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("teamName", formData.teamName);

      // Only append logo if it's a new file or editing without existing logo
      if (logoFile) {
        submitData.append("logo", logoFile);
      } else if (editingTeam && formData.logo) {
        // Keep existing logo URL if no new file is selected
        submitData.append("logo", formData.logo);
      }

      let response;
      if (editingTeam) {
        response = await updateTeam(editingTeam.teamId, submitData);
      } else {
        response = await createTeam(submitData);
      }

      if (response.success) {
        Swal.fire(
          "Success!",
          editingTeam
            ? "Team updated successfully"
            : "Team created successfully",
          "success"
        );
        handleCloseAddModal();
        fetchTeams();
      } else {
        Swal.fire("Error!", response.message || "Operation failed", "error");
      }
    } catch (error) {
      Swal.fire("Error!", "Error: " + error.message, "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      teamName: team.teamName,
      logo: team.logo || "",
    });
    setLogoFile(null);
    setLogoPreview(team.logo || "");
    handleOpenModal();
  };

  const handleDelete = async (teamId, teamName) => {
    const result = await Swal.fire({
      title: "Delete Team",
      text: `Are you sure you want to delete ${teamName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, delete!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteTeam(teamId);
        if (response.success) {
          Swal.fire(
            "Deleted!",
            "Team has been deleted successfully.",
            "success"
          );
          fetchTeams();
        } else {
          Swal.fire(
            "Error!",
            response.message || "Failed to delete team",
            "error"
          );
        }
      } catch (error) {
        Swal.fire("Error!", "Error deleting team: " + error.message, "error");
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTeam(null);
    setFormData({ teamName: "", logo: "" });
    setError("");
    setSuccess("");
  };

  // Modal functions
  const handleOpenModal = () => {
    setShouldRenderModal(true);
    setTimeout(() => setIsModalVisible(true), 5);
  };

  const handleCloseAddModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setShouldRenderModal(false);
      setFormData({ teamName: "", logo: "" });
      setLogoFile(null);
      setLogoPreview("");
      setEditingTeam(null);
    }, 400);
  };

  // Filter teams based on search query
  const filteredTeams = teams.filter((team) => {
    const searchLower = searchQuery.toLowerCase();
    return team.teamName.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <div
        style={{
          minHeight: "100vh",
          padding: "0",
          margin: "0",
          color: "white",
          paddingBottom: 80,
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#001837",
            color: "white",
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "900",
                color: "white",
                margin: "0 0 8px 0",
              }}
            >
              Team Management
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#E5E7EB",
                margin: "0",
              }}
            >
              Manage teams and their players
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Search
              size={20}
              style={{
                position: "absolute",
                left: "12px",
                color: "#6c757d",
              }}
            />
            <input
              type="text"
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 12px 12px 44px",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                backgroundColor: "white",
              }}
            />
          </div>
        </div>

        {/* Add Team Button */}
        <div
          style={{
            backgroundColor: "white",
            padding: "16px",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <button
            onClick={handleOpenModal}
            style={{
              width: "100%",
              backgroundColor: "#001837",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#002855";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#001837";
            }}
          >
            <Plus size={16} />
            Add New Team
          </button>
        </div>

        {/* Teams List */}
        <div
          style={{
            backgroundColor: "white",
            minHeight: "calc(100vh - 300px)",
          }}
        >
          {filteredTeams.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#6c757d",
              }}
            >
              {searchQuery
                ? "No teams found matching your search."
                : "No teams available."}
            </div>
          ) : (
            filteredTeams.map((team, idx) => (
              <div
                key={team.teamId}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "1px solid #eee",
                  padding: "16px 12px",
                  backgroundColor: "white",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: "#666",
                        marginRight: "8px",
                      }}
                    >
                      #{String(idx + 1).padStart(2, "0")}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: "#1a2332",
                      }}
                    >
                      {team.teamName}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#666",
                      marginBottom: "6px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        marginRight: "12px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Users size={14} style={{ marginRight: "4px" }} />
                      {team.playerCount || 0} Players
                    </span>
                    <span style={{ display: "flex", alignItems: "center" }}>
                      Created: {new Date(team.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#666",
                      marginTop: "8px",
                      lineHeight: "1.4",
                    }}
                  >
                    Team ID: <strong>{team.teamId}</strong>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginLeft: "12px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1a2332",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {team.logo ? (
                        <img
                          src={team.logo}
                          alt={team.teamName}
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#001837",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Users size={16} color="white" />
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Team
                    </div>
                  </div>

                  {/* Edit Button */}
                  <button
                    onClick={() => handleEdit(team)}
                    style={{
                      backgroundColor: "#F0F9FF",
                      border: "1px solid #BAE6FD",
                      borderRadius: "8px",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      minWidth: "32px",
                      height: "32px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#E0F2FE";
                      e.target.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#F0F9FF";
                      e.target.style.transform = "scale(1)";
                    }}
                  >
                    <Edit size={14} color="#0284C7" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDelete(team.teamId, team.teamName)}
                    style={{
                      backgroundColor: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: "8px",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      minWidth: "32px",
                      height: "32px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#FEE2E2";
                      e.target.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#FEF2F2";
                      e.target.style.transform = "scale(1)";
                    }}
                  >
                    <Trash2 size={14} color="#EF4444" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Team Modal */}
      {shouldRenderModal && (
        <div
          className={`offcanvas offcanvas-bottom ${
            isModalVisible ? "show" : ""
          }`}
          tabIndex="-1"
          id="addTeamModal"
          aria-labelledby="addTeamModalLabel"
          aria-modal="true"
          role="dialog"
          style={{
            minHeight: "100%",
            background: "transparent",
            transition:
              "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isModalVisible ? "translateY(0)" : "translateY(100%)",
            opacity: isModalVisible ? 1 : 0,
          }}
        >
          <div
            className="offcanvas-header"
            style={{ background: "transparent", justifyContent: "flex-end" }}
          >
            <svg
              onClick={handleCloseAddModal}
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.1)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            >
              <circle
                cx="18"
                cy="18"
                r="18"
                fill="white"
                style={{ fill: "white", fillOpacity: 1 }}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.078 13.0001L12.998 23.0801L23.078 13.0001Z"
                fill="black"
                style={{ fill: "black", fillOpacity: 1 }}
              />
              <path
                d="M23.078 13.0001L12.998 23.0801"
                stroke="black"
                style={{ stroke: "black", strokeOpacity: 1 }}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.078 23.0801L12.998 13.0001L23.078 23.0801Z"
                fill="black"
                style={{ fill: "black", fillOpacity: 1 }}
              />
              <path
                d="M23.078 23.0801L12.998 13.0001"
                stroke="black"
                style={{ stroke: "black", strokeOpacity: 1 }}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div
            className="offcanvas-body border-top mx-0 flex-grow-0"
            style={{
              background: "white",
              height: "100%",
              padding: 0,
              transition:
                "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: isModalVisible ? "translateY(0)" : "translateY(20px)",
              opacity: isModalVisible ? 1 : 0,
            }}
          >
            <div
              style={{
                background: "white",
                zIndex: 99999,
                padding: "17px 0px 0px 0px",
                position: "sticky",
                top: 0,
              }}
            >
              <div
                style={{
                  background: "#001837",
                  color: "white",
                  borderRadius: 16,
                  margin: "0 16px",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Users size={20} color="#001837" />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "white",
                        margin: "0",
                      }}
                    >
                      {editingTeam ? "Edit Team" : "Add New Team"}
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#E5E7EB",
                        margin: "0",
                      }}
                    >
                      {editingTeam
                        ? "Update team information"
                        : "Create a new team with name and logo"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div style={{ backgroundColor: "white", padding: "20px 16px" }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
              >
                {/* Team Name Input */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={formData.teamName}
                    onChange={handleInputChange}
                    name="teamName"
                    placeholder="Enter team name"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      color: "#374151",
                    }}
                    required
                  />
                </div>

                {/* Logo Upload */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Team Logo
                  </label>

                  {/* File Upload Area */}
                  <div
                    style={{
                      border: "2px dashed #D1D5DB",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      backgroundColor: "#F9FAFB",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() =>
                      document.getElementById("logo-upload").click()
                    }
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#001837";
                      e.target.style.backgroundColor = "#F0F9FF";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#D1D5DB";
                      e.target.style.backgroundColor = "#F9FAFB";
                    }}
                  >
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ display: "none" }}
                    />

                    {logoPreview ? (
                      <div>
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            marginBottom: "8px",
                          }}
                        />
                        <p
                          style={{
                            margin: "0",
                            fontSize: "14px",
                            color: "#6B7280",
                          }}
                        >
                          Click to change logo
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "#E5E7EB",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 12px",
                          }}
                        >
                          <Plus size={24} color="#6B7280" />
                        </div>
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: "14px",
                            color: "#374151",
                          }}
                        >
                          Upload Team Logo
                        </p>
                        <p
                          style={{
                            margin: "0",
                            fontSize: "12px",
                            color: "#6B7280",
                          }}
                        >
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>

                  {logoFile && (
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: "12px",
                        color: "#059669",
                      }}
                    >
                      ✓ {logoFile.name} selected
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={modalLoading}
                  style={{
                    width: "100%",
                    backgroundColor: modalLoading ? "#9CA3AF" : "#001837",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    padding: "16px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: modalLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    if (!modalLoading) {
                      e.target.style.backgroundColor = "#002855";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!modalLoading) {
                      e.target.style.backgroundColor = "#001837";
                    }
                  }}
                >
                  {modalLoading ? (
                    <>
                      <Loader />
                      {editingTeam ? "Updating Team..." : "Creating Team..."}
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      {editingTeam ? "Update Team" : "Create Team"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {shouldRenderModal && (
        <div
          className={`offcanvas-backdrop fade ${isModalVisible ? "show" : ""}`}
          style={{
            transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: isModalVisible ? 0.5 : 0,
          }}
        ></div>
      )}
    </div>
  );
};

export default TeamManagement;
